import classNames from "classnames";
import DOMPurify from "dompurify";
import {useState} from "react";
import {FaArrowCircleDown, FaArrowCircleUp} from "react-icons/fa";

import Button from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {TooltipIconRA} from "@/components/overlay/TooltipIconRA.tsx";
import {Paginator} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {SortingDirection} from "@/components/table/Table.types.ts";
import {shortDateTimeUser, shortenText} from "@/lib/format.tsx";
import {usePagination} from "@/lib/hooks/usePagination.ts";

import {CloudStatus, CloudStatusIncidentDetailsDrawer, Incident} from "./CloudStatusIncidentDetailsDrawer.tsx";

import style from "./CloudStatusOutput.module.scss";

interface CloudStatusIncidentHistoryProps {
  className?: string;
  noDataClassName?: string;
  cloudStatus: CloudStatus;
  start: Date;
  end: Date;
  direction?: SortingDirection | null;
}

export const CloudStatusIncidentHistory = ({
  className,
  noDataClassName,
  cloudStatus,
  start,
  end,
  direction,
}: CloudStatusIncidentHistoryProps) => {
  const [selectedIncident, setSelectedIncident] = useState<Incident | undefined>(undefined);
  const {user} = useCurrentUserContext();
  const {pagination, setPagination} = usePagination({useUrlParams: false, defaultLimit: 5});

  const paginate = (array: Array<Incident>, pageSize: number) => {
    const pageCount = Math.ceil(array.length / pageSize);
    return Array.from({length: pageCount}, (_, index) => array.slice(index * pageSize, (index + 1) * pageSize));
  };

  const displayArray = [];
  const history = JSON.parse(cloudStatus.history);
  for (const key in history) {
    const event_json = JSON.parse(history[key]);
    const event_date_str = event_json.start_date || event_json.published || event_json.updated;
    const event_date = Date.parse(event_date_str);
    event_json.parsed_date_str = event_date_str;
    event_json.parsed_date = event_date;
    if (event_date >= start.valueOf() && event_date <= end.valueOf()) {
      displayArray.push(event_json);
    }
  }

  const sortingCoefficient = direction === SortingDirection.Asc ? -1 : 1;
  displayArray.sort((a, b) => (b.parsed_date - a.parsed_date) * sortingCoefficient);

  const paginatedArray = paginate(displayArray, pagination.limit);
  const currentDisplay = paginatedArray[pagination.offset / pagination.limit];

  const closeDrawer = () => {
    setSelectedIncident(undefined);
  };
  return (
    <div className={className}>
      <CloudStatusIncidentDetailsDrawer
        onCancel={closeDrawer}
        selectedIncident={selectedIncident}
        cloudStatus={cloudStatus}
      />
      {!currentDisplay ? (
        <div className={classNames(noDataClassName, "pulse-color-gray-700")}>No incidents found.</div>
      ) : (
        <>
          {currentDisplay.map((incident: Incident, index) => (
            <div key={"incident-history-" + index}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "100px 1fr",
                  gridTemplateRows: "30px auto",
                  gridTemplateAreas: `"icon history" "vertical-line history"`,
                }}
              >
                <div
                  className={classNames("mx-auto", style.historyTimelineIcon)}
                  style={{gridArea: "icon", paddingTop: "2rem"}}
                >
                  {incident.incident_status === "DOWN" && (
                    <TooltipIconRA
                      iconProps={{size: "core"}}
                      icon={FaArrowCircleDown}
                      className={"text-danger bg-body"}
                      title="DOWN"
                    />
                  )}
                  {incident.incident_status === "UP" && (
                    <TooltipIconRA
                      iconProps={{size: "core"}}
                      icon={FaArrowCircleUp}
                      className={"text-success bg-body"}
                      title="UP"
                    />
                  )}
                </div>
                {currentDisplay.length - 1 != index && (
                  <div className={classNames("vr mx-auto", style.vrLine)} style={{gridArea: "vertical-line"}} />
                )}
                <div className="p-2" style={{gridArea: "history"}}>
                  <Card variant="white" className="p-3">
                    <div className="row">
                      <div className="pulseTextH3Bold mb-3">{incident.title}</div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <div className="label">Current Status:</div>
                        <div
                          className={classNames("pulseTextH3Bold", {
                            "text-success": incident.incident_status === "UP",
                            "text-danger": incident.incident_status === "DOWN",
                          })}
                        >
                          {incident.incident_status}
                        </div>
                      </div>
                      <div className="col">
                        <div className="label">Last Updated Time:</div>
                        <div className="pulseTextBodyBold">{shortDateTimeUser(incident.parsed_date_str, user!)}</div>
                      </div>
                      <div className="col">
                        <Button
                          variant={"secondary"}
                          className={"float-end"}
                          onClick={() => {
                            setSelectedIncident(incident);
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div
                        className={style.cloudStatusOutput}
                        dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(shortenText(incident.summary, 400))}}
                      />
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          ))}
          <Paginator
            className="my-2"
            hideOptions
            offset={pagination.offset}
            limit={pagination.limit}
            updatePagination={setPagination}
            total={displayArray.length}
          />
        </>
      )}
    </div>
  );
};
